// ════════════════════════════════════════════════════════════════════
// i18n CATALOG — AdminConfig.js (namespace: config)
//
// One entry per user-facing STATIC UI string rendered by AdminConfig.js.
// Values copied VERBATIM from the page (English) + accurate Modern Standard
// Arabic translations. Dynamic data (workspace names, numbers, currency,
// platform names) is NOT included here — it stays interpolated in the page.
//
// The parent merges en/ar into the runtime catalog (lib/i18n.js). Keys are
// the stable contract referenced by t("config.<key>") in AdminConfig.js.
// ════════════════════════════════════════════════════════════════════

export const en = {
  // ── Page header (SectionHdr) ─────────────────────────────────────────
  "config.heading": "Configuration",
  "config.sub.prefix": "Settings for ",
  "config.sub.thisWorkspace": "this workspace",
  "config.sub.suffix": " · changes save automatically",

  // ── Trial hint banner ────────────────────────────────────────────────
  "config.trial.badge": "Free trial",
  "config.trial.configuringPrefix": "Configuring ",
  "config.trial.oneAggregator": "your aggregator",
  "config.trial.nAggregatorsSuffix": " aggregators",
  "config.trial.note": "Trial includes one aggregator platform. Subscribe to configure all your delivery platforms.",
  "config.trial.upgrade": "Upgrade →",

  // ── Fixed costs card ─────────────────────────────────────────────────
  "config.fixed.titlePrefix": "Monthly fixed costs — ",
  "config.fixed.desc": "Used for break-even on Overview. Synced with mobile app.",
  "config.saved": "✓ Saved",
  "config.saving": "Saving...",
  "config.fixed.rent": "Rent / rates",
  "config.fixed.wages": "Wages / labour",
  "config.fixed.utilities": "Utilities / bills",
  "config.fixed.other": "Other fixed",
  "config.fixed.totalLabel": "Total monthly fixed",
  "config.fixed.perMonth": "/mo",
  "config.fixed.tip": "Tip: changes save automatically when you click out of a field.",

  // ── Tax & cost rates card ────────────────────────────────────────────
  "config.rates.titlePrefix": "Tax & cost rates — ",
  "config.rates.desc": "VAT and COGS specific to this workspace. Set your own values — calculations apply 0% if not configured.",
  "config.rates.vatLabel": "VAT rate %",
  "config.rates.vatHintUae": "UAE standard = 5%",
  "config.rates.vatHintUk": "UK standard = 20%",
  "config.rates.vatHintIn": "India restaurant GST = 5%",
  "config.rates.cogsLabel": "COGS % of gross",
  "config.rates.cogsHint": "Estimated cost of goods as % of gross revenue",
  "config.rates.vatOnFeesTitle": "VAT on aggregator fees",
  "config.rates.vatOnFeesDescPrefix": "% VAT is automatically added on top of commission, card fee, CPC, and campaign fees, then treated as recoverable input VAT. This matches how UK and UAE aggregators (Deliveroo, Uber Eats, Talabat, Careem) bill their fees.",
  "config.rates.footnote": "💡 Changes save automatically when you click out of a field. See real numbers in Insights or test specific items in Price Tool.",

  // ── Per-platform settings card ───────────────────────────────────────
  "config.platform.title": "Per-platform settings",
  "config.platform.emptyDesc": "No platforms set up yet. Create a workspace to get started.",
  "config.platform.desc": "Commission and card fee for the selected platform. Switch platforms via the pill bar above.",
};

export const ar = {
  // ── Page header (SectionHdr) ─────────────────────────────────────────
  "config.heading": "الإعدادات",
  "config.sub.prefix": "إعدادات ",
  "config.sub.thisWorkspace": "مساحة العمل هذه",
  "config.sub.suffix": " · يتم الحفظ تلقائيًا",

  // ── Trial hint banner ────────────────────────────────────────────────
  "config.trial.badge": "تجربة مجانية",
  "config.trial.configuringPrefix": "جارٍ إعداد ",
  "config.trial.oneAggregator": "منصة التجميع الخاصة بك",
  "config.trial.nAggregatorsSuffix": " منصات تجميع",
  "config.trial.note": "تتضمّن التجربة منصة تجميع واحدة. اشترك لإعداد جميع منصات التوصيل الخاصة بك.",
  "config.trial.upgrade": "الترقية ←",

  // ── Fixed costs card ─────────────────────────────────────────────────
  "config.fixed.titlePrefix": "التكاليف الثابتة الشهرية — ",
  "config.fixed.desc": "تُستخدم لحساب نقطة التعادل في نظرة عامة. متزامنة مع تطبيق الجوال.",
  "config.saved": "✓ تم الحفظ",
  "config.saving": "جارٍ الحفظ...",
  "config.fixed.rent": "الإيجار / الرسوم",
  "config.fixed.wages": "الأجور / العمالة",
  "config.fixed.utilities": "المرافق / الفواتير",
  "config.fixed.other": "تكاليف ثابتة أخرى",
  "config.fixed.totalLabel": "إجمالي الثابت الشهري",
  "config.fixed.perMonth": "/شهر",
  "config.fixed.tip": "نصيحة: يتم حفظ التغييرات تلقائيًا عند النقر خارج الحقل.",

  // ── Tax & cost rates card ────────────────────────────────────────────
  "config.rates.titlePrefix": "معدّلات الضريبة والتكلفة — ",
  "config.rates.desc": "ضريبة القيمة المضافة وتكلفة البضاعة المباعة الخاصة بمساحة العمل هذه. حدّد قيمك الخاصة — تطبّق الحسابات 0٪ إذا لم تُضبط.",
  "config.rates.vatLabel": "معدّل ضريبة القيمة المضافة ٪",
  "config.rates.vatHintUae": "المعدّل القياسي للإمارات = 5%",
  "config.rates.vatHintUk": "المعدّل القياسي للمملكة المتحدة = 20%",
  "config.rates.vatHintIn": "ضريبة السلع والخدمات للمطاعم في الهند = 5%",
  "config.rates.cogsLabel": "تكلفة البضاعة المباعة ٪ من الإجمالي",
  "config.rates.cogsHint": "التكلفة التقديرية للبضائع كنسبة ٪ من إجمالي الإيرادات",
  "config.rates.vatOnFeesTitle": "ضريبة القيمة المضافة على رسوم منصات التجميع",
  "config.rates.vatOnFeesDescPrefix": "٪ من ضريبة القيمة المضافة تُضاف تلقائيًا فوق العمولة ورسوم البطاقة وتكلفة النقرة ورسوم الحملات، ثم تُعامل كضريبة مدخلات قابلة للاسترداد. يطابق هذا طريقة احتساب منصات التجميع في المملكة المتحدة والإمارات (Deliveroo، Uber Eats، Talabat، Careem) لرسومها.",
  "config.rates.footnote": "💡 يتم حفظ التغييرات تلقائيًا عند النقر خارج الحقل. اطّلع على الأرقام الفعلية في رؤى+ أو اختبر أصنافًا محدّدة في أداة التسعير.",

  // ── Per-platform settings card ───────────────────────────────────────
  "config.platform.title": "إعدادات كل منصة",
  "config.platform.emptyDesc": "لم يتم إعداد أي منصات بعد. أنشئ مساحة عمل للبدء.",
  "config.platform.desc": "العمولة ورسوم البطاقة للمنصة المحددة. بدّل بين المنصات عبر شريط الأزرار أعلاه.",
};
